export type FinanceChatEvent =
  | { type: "meta"; session_id: string }
  | { type: "stage"; stage: "reasoning" | "message" | "tool" | string }
  | { type: "thinking"; content: string }
  | { type: "message"; content: string }
  | {
      type: "tool_call_delta";
      index: number;
      id: string;
      name: string;
      arguments_delta: string;
    }
  | { type: "tool_call"; id: string; name: string; arguments: string }
  | { type: "tool_result"; id: string; name: string; content: string }
  | { error: string };

function extractSseFrames(buffer: string) {
  const chunks = buffer.split("\n\n");
  if (chunks.length <= 1) return { frames: [] as string[], rest: buffer };
  const rest = chunks[chunks.length - 1] ?? "";
  const frames = chunks
    .slice(0, -1)
    .map((x) => x.trim())
    .filter(Boolean);
  return { frames, rest };
}

function parseSseDataLines(frameText: string) {
  const lines = frameText.split("\n");
  const dataLines: string[] = [];
  for (const ln of lines) {
    const trimmed = ln.trim();
    if (!trimmed) continue;
    if (trimmed.startsWith("data:")) {
      dataLines.push(trimmed.slice("data:".length).trimStart());
    }
  }
  return dataLines.join("\n");
}

export async function streamFinanceChat(
  payload: {
    session_id?: string;
    message: string;
    history_limit?: number;
  },
  opts: {
    signal?: AbortSignal;
    onEvent: (evt: FinanceChatEvent | { type: "done" }) => void;
  }
) {
  const res = await fetch("/api/v1/finance/chat", {
    method: "POST",
    headers: {
      Accept: "text/event-stream",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
    signal: opts.signal,
  });

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(body || `${res.status} ${res.statusText}`);
  }

  if (!res.body) {
    throw new Error("Missing response body.");
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const extracted = extractSseFrames(buffer);
    buffer = extracted.rest;

    for (const frame of extracted.frames) {
      const data = parseSseDataLines(frame);
      if (!data) continue;

      if (data === "[DONE]") {
        opts.onEvent({ type: "done" });
        continue;
      }

      try {
        opts.onEvent(JSON.parse(data) as FinanceChatEvent);
      } catch {
      }
    }
  }

  const final = buffer.trim();
  if (final) {
    const data = parseSseDataLines(final);
    if (data === "[DONE]") {
      opts.onEvent({ type: "done" });
    } else if (data) {
      try {
        opts.onEvent(JSON.parse(data) as FinanceChatEvent);
      } catch {
      }
    }
  }
}

// ── News Analysis ─────────────────────────────────────────────────────────────

export type SectorImpact = {
  sector: string;
  change_percent: number;
};

export type NewsAnalysisEvent =
  | { type: "meta"; session_id: string }
  | { type: "stage"; stage: string }
  | { type: "thinking"; content: string }
  | { type: "message"; content: string }
  | { type: "heatmap_data"; sectors: SectorImpact[] }
  | { type: "done" }
  | { error: string };

export async function streamNewsAnalysis(
  payload: {
    news_url: string;
    news_title: string;
    news_summary: string;
    sector_id: string;
    sector_label: string;
  },
  opts: {
    signal?: AbortSignal;
    onEvent: (evt: NewsAnalysisEvent) => void;
  }
) {
  const res = await fetch("/api/v1/finance/news/analyze", {
    method: "POST",
    headers: {
      Accept: "text/event-stream",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
    signal: opts.signal,
  });

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(body || `${res.status} ${res.statusText}`);
  }

  if (!res.body) {
    throw new Error("Missing response body.");
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const extracted = extractSseFrames(buffer);
    buffer = extracted.rest;

    for (const frame of extracted.frames) {
      const data = parseSseDataLines(frame);
      if (!data) continue;

      if (data === "[DONE]") {
        opts.onEvent({ type: "done" });
        continue;
      }

      try {
        opts.onEvent(JSON.parse(data) as NewsAnalysisEvent);
      } catch {
        // ignore parse errors
      }
    }
  }

  const final = buffer.trim();
  if (final) {
    const data = parseSseDataLines(final);
    if (data === "[DONE]") {
      opts.onEvent({ type: "done" });
    } else if (data) {
      try {
        opts.onEvent(JSON.parse(data) as NewsAnalysisEvent);
      } catch {
        // ignore
      }
    }
  }
}